export const TV_PALETTE = {
  bg: '#EEF3FB',
  card: '#FFFFFF',
  cardSoft: '#F4F8FD',
  navy: '#023373',
  navyDeep: '#011F52',
  border: '#DDE6F0',
  borderSoft: '#EEF3FB',
  textMuted: '#6F87A6',
  textSubtle: '#8FA8C0',
  textGhost: '#B0C4D8',
  /** Coincide con bg-green-50 / green-100 del fixture (filas clasificadas) */
  qualifierGreenBg: '#f0fdf4',
  qualifierGreenBgLead: '#dcfce7',
  accent: '#4BD32F',
  accent2: '#FF5A1D',
  /** Modo TV top bar — claro para máximo contraste con el cuerpo */
  hdrBg: '#FFFFFF',
} as const;

export const TV_HEADER_HEIGHT = 56;
/** Altura base del carril “Últimos resultados” (escala con TV_SCALE_MULTIPLIERS) */
export const TV_RESULTS_HEIGHT = 184;

/**
 * Escalas de tipografía y espaciado del Modo TV.
 * El usuario elige una desde el header; el multiplicador se aplica a fontSize,
 * paddings, alturas y anchos relevantes vía el helper `s(n)` del TVScaleContext.
 *
 * `M` reproduce los valores actuales (línea base — refactor invisible).
 */
export const TV_SCALE_KEYS = ['S', 'M', 'L', 'XL'] as const;
export type TVScaleKey = (typeof TV_SCALE_KEYS)[number];

export const TV_SCALE_MULTIPLIERS: Record<TVScaleKey, number> = {
  S: 0.85,
  M: 1,
  L: 1.2,
  XL: 1.45,
};

export const TV_SCALE_DEFAULT: TVScaleKey = 'M';
export const TV_SCALE_STORAGE_KEY = 'tv-mode:scale';

/**
 * Modos de visualización del nombre de los jugadores en el Modo TV.
 *  - `abbreviated`: "Reyes E. / Cabrera M." (apellido + inicial — comportamiento histórico)
 *  - `full`: "Esteban Reyes / Marcos Cabrera" (nombre completo, primero + último)
 */
export const TV_NAME_MODES = ['abbreviated', 'full'] as const;
export type TVNameMode = (typeof TV_NAME_MODES)[number];

export const TV_NAME_MODE_DEFAULT: TVNameMode = 'abbreviated';
export const TV_NAME_MODE_STORAGE_KEY = 'tv-mode:nameMode';
/** Partidos mostrados en el carril (ordenados del más reciente al más antiguo en datos) */
export const TV_RESULTS_MAX_ITEMS = 10;
/** Velocidad del scroll infinito horizontal de resultados (px/s) */
export const TV_RESULTS_TICKER_PX_PER_SEC = 44;
/** Tiempo que se muestra cada “Nuevo resultado” antes del carril (cola FIFO) */
export const TV_RESULTS_SPOTLIGHT_MS = 15_000;
/** Pill tipo “en vivo” para el aviso de nuevo resultado (fondo rojo‑naranja) */
export const TV_RESULTS_SPOTLIGHT_BADGE_BG = '#E8452E';
/**
 * Ancho base de la columna “Próximos partidos” (px, pre‑escala).
 * El usuario puede redimensionarla arrastrando el borde izquierdo —
 * el valor preferido se guarda en localStorage (clave de abajo) y se
 * multiplica luego por la escala activa al renderizar.
 */
export const TV_UPCOMING_COLUMN_WIDTH = 272;
export const TV_UPCOMING_COLUMN_MIN_WIDTH = 240;
export const TV_UPCOMING_COLUMN_MAX_WIDTH = 520;
export const TV_UPCOMING_COLUMN_WIDTH_STORAGE_KEY = 'tv-mode:upcomingWidth';
export const TV_GROUP_CAROUSEL_INTERVAL_MS = 5000;
/**
 * Auto-scroll vertical del cuadro eliminatorio cuando el contenido supera la
 * altura del viewport. Avanza por páginas con overlap (85% del viewport por
 * paso) y al llegar al final vuelve al inicio.
 */
export const TV_BRACKET_AUTOSCROLL_INTERVAL_MS = 6000;
export const TV_BRACKET_AUTOSCROLL_TRANSITION_MS = 800;
export const TV_BRACKET_AUTOSCROLL_PAGE_RATIO = 0.85;
/** Máximo de grupos visibles si aún no hay medición de altura (fallback) */
export const TV_GROUPS_PER_PAGE = 4;
/**
 * Altura mínima de una tarjeta de grupo para 4 filas de tabla (cabecera + PTS)
 * sin recortar equipos típicos (padding 12×2 + título + thead + 4 filas ~30px).
 */
export const TV_GROUP_CARD_MIN_HEIGHT_PX = 244;
/** Separación vertical entre tarjetas en el carrusel */
export const TV_GROUP_CAROUSEL_GAP_PX = 8;
/** Espacio reservado bajo la columna cuando hay indicadores de slide */
export const TV_GROUP_CAROUSEL_DOTS_RESERVE_PX = 32;
/** Duración del desplazamiento vertical del bloque de grupos (carrusel) */
export const TV_GROUP_CAROUSEL_TRACK_MS = 680;
